import * as React from "react"
import {LucideIcon} from "lucide-react"
import {ButtonGroup} from "@/components/ui/button-group"
import {type ContextualAccountAccessDialogContext} from "@/main/react/account_access/AccountAccessDialogContext"
import {Localizer} from "@/main/react/Types"
import {CtaToggle} from "./CtaToggle"
import {CtaCountFeedback, CtaInteractionEffect, CtaSize} from "./ctaShared"

type VoteContainerBaseProps = {
    leftButtonText?: string | null
    leftButtonTitle?: string | null
    leftButtonIcon: LucideIcon
    leftButtonCount?: number
    leftButtonActive?: boolean
    disabled?: boolean
    disabledReason?: string | null
    leftButtonData?: string | null
    leftButtonExtraClass?: string
    leftButtonActiveClass?: string
    leftInteractionEffect?: CtaInteractionEffect
    leftCountFeedback?: CtaCountFeedback
    rightButtonText?: string | null
    rightButtonTitle?: string | null
    rightButtonIcon: LucideIcon
    rightButtonCount?: number
    displayTextOnMobile?: boolean
    rightButtonActive?: boolean
    rightButtonData?: string | null
    rightButtonExtraClass?: string
    rightButtonActiveClass?: string
    rightInteractionEffect?: CtaInteractionEffect
    rightCountFeedback?: CtaCountFeedback
    requiresLogin?: boolean
    onLeftClick?: (ev?: Event | React.MouseEvent<HTMLButtonElement>) => void | Promise<void>
    onRightClick?: (ev?: Event | React.MouseEvent<HTMLButtonElement>) => void | Promise<void>
    size?: CtaSize
    /**
     * Injected typed localizer. Kept on the contract because every product call site passes it and
     * a later accessible-name key would need it; the control itself names each arrow from its title
     * and its count (see `buildArrowLabel`), which needs no catalog lookup. Optional only so test
     * doubles and the design-system showcase can mount a bare control.
     */
    localizer?: Localizer
}

type VoteContainerLoginGateProps =
    | {
        requiresLogin?: false
        accountAccessDialog?: never
    }
    | {
        requiresLogin: true
        accountAccessDialog: ContextualAccountAccessDialogContext
    }

export type VoteContainerProps = VoteContainerBaseProps & VoteContainerLoginGateProps

/**
 * Accessible name for one arrow: its title and its exact count, `Upvote: 12`, the same shape the
 * static own-work presentations build in `ThoughtCardDisplayer` and `DiscussionButtons`
 * (`${title}: ${count}`), so the mounted control and the static one read the same to assistive
 * technology. Without it the button's name would be computed from its content, which is the bare
 * count. No catalog key is involved: the title is already a localized string at every call site.
 */
function buildArrowLabel(title: string | null, count: number) {
    return title == null ? undefined : `${title}: ${count}`
}

export function VoteContainer({
    leftButtonText = null,
    leftButtonTitle = null,
    leftButtonIcon,
    leftButtonCount = 0,
    leftButtonActive = false,
    leftButtonData = null,
    leftButtonExtraClass = "",
    leftButtonActiveClass = undefined,
    leftInteractionEffect = "weight",
    leftCountFeedback = "none",
    rightButtonText = null,
    rightButtonTitle = null,
    rightButtonIcon,
    rightButtonCount = 0,
    rightButtonActive = false,
    rightButtonData = null,
    rightButtonExtraClass = "",
    rightButtonActiveClass = undefined,
    rightInteractionEffect = "weight",
    rightCountFeedback = "none",
    requiresLogin = false,
    accountAccessDialog,
    onLeftClick,
    onRightClick,
    size = "md",
    // Destructured so it never reaches the DOM through `...rest`; see the prop's doc comment.
    localizer: _localizer,
    disabled = false,
    ...rest
}: VoteContainerProps) {
    const wrapPressedChange = (handler?: VoteContainerProps["onLeftClick"]) => {
        if (!handler) return undefined
        return () => handler()
    }

    const loginGateProps = requiresLogin
        ? { requiresLogin: true as const, accountAccessDialog: accountAccessDialog! }
        : { requiresLogin: false as const }

    // Two counts, one inside each arrow, always shown (including 0) so the control keeps a stable
    // width from the first vote onwards. There is deliberately NO merged `up − down` number and no
    // attribute carrying one: the net shipped 2026-07-26 and was reversed on 2026-08-25 (member-safety
    // design C′), because a negative net reads as a verdict on the author and the card's own-work
    // presentation (`ThoughtCardDisplayer`, `DiscussionButtons`) already showed the two counts, so
    // the mounted control and the static one disagreed on the same row. Each side keeps its own
    // count-pulse feedback through `CtaToggle`.
    const leftCount = Number.isFinite(leftButtonCount) ? leftButtonCount : 0
    const rightCount = Number.isFinite(rightButtonCount) ? rightButtonCount : 0

    return (
        <ButtonGroup className="items-center">
            <CtaToggle
                icon={leftButtonIcon}
                text={leftButtonText}
                title={leftButtonTitle ?? undefined}
                ariaLabel={buildArrowLabel(leftButtonTitle, leftCount)}
                buttonCount={leftCount}
                buttonActive={leftButtonActive}
                buttonData={leftButtonData ?? undefined}
                extraClass={leftButtonExtraClass}
                activeClass={leftButtonActiveClass}
                interactionEffect={leftInteractionEffect}
                countFeedback={leftCountFeedback}
                disabled={disabled}
                roundedLeft
                {...loginGateProps}
                onPressedChange={wrapPressedChange(onLeftClick)}
                size={size}
                {...rest}
            />
            <CtaToggle
                icon={rightButtonIcon}
                text={rightButtonText}
                title={rightButtonTitle ?? undefined}
                ariaLabel={buildArrowLabel(rightButtonTitle, rightCount)}
                buttonCount={rightCount}
                buttonActive={rightButtonActive}
                buttonData={rightButtonData ?? undefined}
                extraClass={rightButtonExtraClass}
                activeClass={rightButtonActiveClass}
                interactionEffect={rightInteractionEffect}
                countFeedback={rightCountFeedback}
                disabled={disabled}
                roundedRight
                {...loginGateProps}
                onPressedChange={wrapPressedChange(onRightClick)}
                size={size}
                {...rest}
            />
        </ButtonGroup>
    )
}
